import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useBoolean } from 'react-hanger';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
    GridHeaderSelectionChangeEvent,
    GridSelectionChangeEvent,
    GridFilterChangeEvent,
} from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import style from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { CriticalVariableListItemFields } from '@queries/generated/fragments.generated';

interface AssignCtqToRiskDialogProps {
    riskStatement: string;
    assignableCtqs: CriticalVariableListItemFields[];
    assigning: boolean;
    onClose: () => void;
    onSubmit: (selectedCtqIds: string[], close: boolean) => void;
    onAddCtqOpen: () => void;
    preselectedCtqIds?: string[];
}

type SelectState = SelectDescriptor;

const AssignCtqToRiskDialog = ({
    riskStatement,
    assignableCtqs,
    assigning,
    onClose,
    onSubmit,
    onAddCtqOpen,
    preselectedCtqIds,
}: AssignCtqToRiskDialogProps) => {
    const { t: tDialog } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors' });
    const { t: tTitle } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs' });
    const { TooltipCell } = useGridCustomCell();

    const {
        value: clearSelectedRows,
        setTrue: setClearSelectedRowsTrue,
        setFalse: setClearSelectedRowsFalse,
    } = useBoolean(false);

    const [select, setSelect] = useState<SelectState>({});
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor>({
        logic: 'and',
        filters: [],
    });

    const rows = useMemo(() => assignableCtqs ?? [], [assignableCtqs]);

    useEffect(() => {
        setClearSelectedRowsTrue();
        setSelect({});
    }, [rows, setClearSelectedRowsTrue]);

    useEffect(() => {
        if (!rows.length || !preselectedCtqIds?.length) {
            return;
        }

        const map: SelectDescriptor = {};
        rows.forEach((r) => {
            if (preselectedCtqIds.includes(r.id)) {
                map[r.id] = true;
            }
        });

        setSelect(map);
        setClearSelectedRowsFalse();
    }, [rows, preselectedCtqIds, setClearSelectedRowsFalse]);

    const onSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onSubmitAssign = (close: boolean) => {
        if (!rows.length) return;

        const selectedRowKeys = Object.keys(select).filter((k) => select[k] === true);
        if (!selectedRowKeys.length) return;

        const selectedCtqIds = rows.filter((r) => selectedRowKeys.includes(r.id)).map((r) => r.id);

        setSelect({});
        setClearSelectedRowsTrue();
        onSubmit(selectedCtqIds, close);
    };

    const selectedCount = Object.keys(select).filter((k) => select[k] === true).length;
    const formActionsEnabled = selectedCount > 0 && !assigning;
    const newDisabled = selectedCount > 0 || assigning;
    const statement = riskStatement ?? '';
    const dialogTitle = `${tTitle('assignCtqs')}`;

    const onCloseHandler = (event?: unknown) => {
        const action = (event as { action?: string } | undefined)?.action;
        if (action === 'button') {
            if (newDisabled) return;
            setClearSelectedRowsTrue();
            setSelect({});
            onAddCtqOpen();
        } else {
            onClose();
        }
    };

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            locked: true,
            filterable: false,
        },
        {
            id: 'description',
            field: 'description',
            title: tDialog('grid.columns.description'),
            customColumn: true,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
    ];

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="assign-ctq-to-risk-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAssign(false)}
            primaryBtnLabel={tDialog('dialogs.assignRisks.buttons.assign')}
            primaryBtnDisabled={!formActionsEnabled}
            secondaryBtnLabel={tDialog('dialogs.assignRisks.buttons.new')}
            secondaryBtnDisabled={newDisabled}
            onAdditionalAction={() => onSubmitAssign(true)}
            additionalBtnLabel={tDialog('dialogs.assignRisks.buttons.assignAndClose')}
            additionalBtnDisabled={!formActionsEnabled}
            initialHeight={520}
            initialWidth={722}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                <div>
                    {statement.length <= 600 ? (
                        <>
                            <span className="k-font-medium">{tDialog('dialogs.unassignRisk.riskStatement')} - </span>
                            {statement}
                        </>
                    ) : (
                        <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                            <span className="k-font-medium">{tDialog('dialogs.assignRisks.columns.statement')} - </span>
                            <span title={statement}>{`${statement.slice(0, 600)}...`}</span>
                        </Tooltip>
                    )}
                </div>
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={tDialog('grid.columns.description')}
                        maxHeight="auto"
                        testDataId="assign-ctq-grid"
                        data={rows}
                        columns={columns}
                        filterable
                        loading={assigning}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRows}
                        useCardWrapper={false}
                        filter={filterState}
                        onFilterChange={async (e: GridFilterChangeEvent) => {
                            const next = (e.filter || { logic: 'and', filters: [] }) as CompositeFilterDescriptor;
                            setFilterState(next);
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                        clearFilter={async () => {
                            setFilterState({ logic: 'and', filters: [] });
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AssignCtqToRiskDialog;
